import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from '@/stores/useSuiteStore'

export type BillingStatus = 'NOT_INVOICED' | 'PARTIALLY_INVOICED' | 'INVOICED'
export type PaymentStatus = 'UNPAID' | 'PARTIALLY_PAID' | 'PAID'

interface DashboardInvoice {
  uuid: string
  name: string | null
  referenceExterne: string | null
  invoiceDate: string | null
  invoiceDateDue: string | null
  amountTotal: number
  moveType: string | null
  state: string | null
  paymentState: string | null
}

export interface QuoteBillingRow {
  quoteUuid: string
  quoteReference: string
  customerReference: string | null
  companyUuid: string
  companyName: string | null
  salesEngineerUuid: string
  salesEngineerName: string | null
  signedAt: string | null
  totalCa: number
  billedAmount: number
  invoiceDateMin: string | null
  invoiceDateMax: string | null
  invoiceDateDueMax: string | null
  billingStatus: BillingStatus
  paymentStatus: PaymentStatus
  invoices: DashboardInvoice[]
}

export interface SalesEngineerOption {
  uuid: string
  firstName: string | null
  lastName: string | null
  label: string
}

export interface DashboardFilters {
  invoiceDateFrom?: string
  invoiceDateTo?: string
  signedFrom?: string
  signedTo?: string
  salesEngineerUuid?: string
  search?: string
  status?: string
  page?: number
  pageSize?: number
}

export const useQuoteBillingDashboardStore = defineStore('quoteBillingDashboard', () => {
  const suiteStore = useSuiteStore()

  const rows = ref<QuoteBillingRow[]>([])
  const total = ref(0)
  const salesEngineers = ref<SalesEngineerOption[]>([])
  const isLoading = ref(false)

  async function fetchDashboard(filters: DashboardFilters) {
    isLoading.value = true
    try {
      const q = new URLSearchParams()
      if (filters.invoiceDateFrom) q.set('invoiceDateFrom', filters.invoiceDateFrom)
      if (filters.invoiceDateTo) q.set('invoiceDateTo', filters.invoiceDateTo)
      if (filters.signedFrom) q.set('signedFrom', filters.signedFrom)
      if (filters.signedTo) q.set('signedTo', filters.signedTo)
      if (filters.salesEngineerUuid) q.set('salesEngineerUuid', filters.salesEngineerUuid)
      if (filters.search) q.set('search', filters.search)
      if (filters.status) q.set('status', filters.status)
      if (filters.page !== undefined) q.set('page', String(filters.page))
      if (filters.pageSize !== undefined) q.set('pageSize', String(filters.pageSize))

      const res = await suiteStore.suiteFetch(`quote-billing/dashboard?${q}`, {
        method: 'GET',
        credentials: 'include',
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        const json = await res.json()
        rows.value = json.data ?? []
        total.value = json.total ?? 0
      }
    } finally {
      isLoading.value = false
    }
  }

  async function fetchSalesEngineers() {
    const res = await suiteStore.suiteFetch('quote-billing/sales-engineers', {
      method: 'GET',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      salesEngineers.value = (await res.json()) ?? []
    }
  }

  return {
    rows,
    total,
    salesEngineers,
    isLoading,
    fetchDashboard,
    fetchSalesEngineers,
  }
})
